import _ from 'lodash'
import { useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Image } from '../../components/Image'
import { PriceTotal } from '../../components/PriceTotal'
import { ColumnType, makeStyledDateCell } from '../../components/Table'
import { Text } from '../../components/Text'
import { TransactionsDataTable } from '../../containers/Transactions/TransactionsDataTable'
import { TransactionsDataTableHeader } from '../../containers/Transactions/TransactionsDataTableHeader'
import { getPricesMode } from '../../redux/config'
import { DialogActions } from '../../redux/dialog'
import { getDomains, DomainType, DomainsActions } from '../../redux/domains'
import { OrderData } from '../../redux/order'
import { RootState } from '../../redux/types'
import { getContractType, canSeePrices } from '../../redux/user/selectors'
import { getDomainDescription } from '../../utils/helpers'

const chevronIcon = <Image alt="arrowRight" type="chevronRight" />
const cardIcon = <Image alt="creditCardBlue" type="creditCardBlue" />

const domains = [DomainType.OrderStatus, DomainType.PaymentMethod]

export const TransactionsData = () => {
  const { t, i18n } = useTranslation(['account', 'core', 'table'])
  const tf = useCallback((key: string) => t(`form:${key}`), [t])

  const dispatch = useDispatch()

  const pricesMode = useSelector(getPricesMode)
  const contractType = useSelector(getContractType)

  useEffect(() => {
    dispatch(
      DomainsActions.loadDomains({
        language: i18n.language,
        domainTypes: domains
      })
    )
  }, [dispatch, i18n.language])

  const domainsData = useSelector((state: RootState) =>
    getDomains(state, {
      domains,
      language: i18n.language
    })
  )

  const renderCompanyName = (name: string) => (
    <Text bold color="primary">
      {name}
    </Text>
  )

  const cellClickHandler = useCallback(
    (value: OrderData) => ({
      style: { cursor: 'pointer' },
      onClick: () =>
        dispatch(
          DialogActions.showDialogWithData({
            dialog: 'TransactionsDialog',
            data: {
              orderId: value.orderId
            }
          })
        )
    }),
    [dispatch]
  )

  const showPrices = useSelector(canSeePrices)

  const tableColumns: ColumnType<OrderData>[] = useMemo(
    () =>
      _.compact([
        {
          id: 'icon',
          title: '',
          dataIndex: 'orderId',
          sorter: false,
          visibilityType: 'non-selectable',
          // eslint-disable-next-line react/display-name
          render: () => cardIcon
        },
        {
          id: 'orderId',
          title: tf('OrderId'),
          dataIndex: 'orderId',
          sorter: true,
          visibilityType: 'fixed',
          onCell: cellClickHandler,
          // eslint-disable-next-line react/display-name
          render: renderCompanyName
        },
        {
          id: 'placedAt',
          title: tf('OrderDate'),
          dataIndex: 'placedAt',
          sorter: true,
          render: makeStyledDateCell({ dateStyle: 'medium', timeStyle: undefined })
        },
        {
          id: 'orderStatus',
          title: tf('Status'),
          dataIndex: 'orderStatus',
          sorter: true,
          render: (value: string) => getDomainDescription(DomainType.OrderStatus, value, domainsData)
        },
        showPrices
          ? {
              id: 'totalNetValueFormatted',
              title: tf('TotalAmountPaid'),
              dataIndex: 'orderId',
              sorter: true,
              render: (value: string, order: OrderData) => {
                return PriceTotal({
                  pricesMode,
                  contractType,
                  grossValue: order.totalValueWithTaxFormatted,
                  netValue: order.totalNetValueFormatted
                })
              }
            }
          : undefined,
        {
          id: 'paymentMethod',
          title: tf('PaymentType'),
          dataIndex: 'paymentMethod',
          sorter: true,
          render: (value: string) => getDomainDescription(DomainType.PaymentMethod, value, domainsData)
        },
        {
          id: 'icon',
          title: '',
          dataIndex: 'orderId',
          sorter: false,
          width: '24px',
          visibilityType: 'non-selectable',
          // eslint-disable-next-line react/display-name
          onCell: cellClickHandler,
          render: () => chevronIcon
        }
      ]),
    [tf, cellClickHandler, domainsData, contractType, pricesMode, showPrices]
  )

  return (
    <>
      <TransactionsDataTableHeader tableColumns={tableColumns} />
      <TransactionsDataTable tableColumns={tableColumns} />
    </>
  )
}

export default TransactionsData
